import { ChoiceButtonInput, ChoiceButtonOption } from '@app/app/_components/Input/ChoiceButtonInput';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { splitNumber } from '@app/lib/split-number/splitNumber';
import { useLocale, useTranslations } from 'next-intl';
import { ChangeEvent, useMemo } from 'react';
import { Message } from '@app/app/_components/Message/Message';
import { HomeInsuranceRiskPackage } from '@app/domain/model/eshop/home/HomeInsuranceRiskPackage';
import { IncludedCovers } from '@app/app/esales/home/_sections/steps/InsuranceRisksStep/IncludedCovers';
import { Button } from '@app/app/_components/Button/Button';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { InsuranceRisksLabel } from '@app/app/esales/home/_sections/steps/InsuranceRisksStep/InsuranceRisksLabel';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    risks: HomeInsuranceRiskPackage[];
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    value?: string;
    defaultChecked?: string;
}

export function InsuranceRisksStep({ risks, onChange, value, defaultChecked }: Props) {
    const t = useTranslations();
    const parameters = useAppParameters();
    const locale = useLocale();

    const availableCovers = parameters.settings.general.esalesHomeAvailableEsalesRisks;

    const options: ChoiceButtonOption[] = useMemo(() => {
        return risks.map(risk => {
            const hasDiscount =
                risk.premiumAfterDiscount != null &&
                risk.premium != null &&
                risk.premiumAfterDiscount > 0 &&
                risk.premiumAfterDiscount < risk.premium;

            const displayPrice = hasDiscount ? risk.premiumAfterDiscount! : (risk.premium ?? 0);
            const splittedPrice = splitNumber(displayPrice);

            let discountPercentage = 0;
            if (hasDiscount && risk.premium) {
                discountPercentage = ((risk.premium - risk.premiumAfterDiscount!) / risk.premium) * 100;
            }

            return {
                value: risk.code ?? '',
                label: (
                    <InsuranceRisksLabel
                        risk={risk}
                        splittedPrice={splittedPrice}
                        hasDiscount={hasDiscount}
                        discountPercentage={discountPercentage}
                    />
                ),
                details: (
                    <div className="flex flex-col gap-4">
                        <IncludedCovers
                            covers={risk.includedCovers.filter(cover => availableCovers.includes(cover.code))}
                        />
                    </div>
                ),
            };
        });
    }, [risks, availableCovers]);

    return (
        <div className="flex flex-col gap-6">
            <ChoiceButtonInput
                name="homeInsuranceRisk"
                options={options}
                onChange={onChange}
                value={value}
                defaultChecked={defaultChecked}
            />
            <Message
                className="md:flex-row md:items-start"
                variant="info"
                addon={
                    <Button
                        preventDefault
                        variant="outlined"
                        size="tiny"
                        target="_blank"
                        href={
                            locale === 'lt'
                                ? parameters.eshopHomeInsuranceInfoUrls.compareUrlLt
                                : parameters.eshopHomeInsuranceInfoUrls.compareUrlEn
                        }
                        rightAddon={<ArrowRightIcon className="size-4" />}>
                        {t('eshop.general.compare')}
                    </Button>
                }>
                <div className="flex flex-col gap-2">
                    <Paragraph isBold>{t('eshop.general.compareInsurances')}</Paragraph>
                    <Paragraph size="small">{t('eshop.home.compareInsurancesMessage')}</Paragraph>
                </div>
            </Message>
        </div>
    );
}
